/**
 * Study Chat Page
 */

import { useState, useEffect, useRef } from 'react';
import { StudyLayout } from '../../components/study/StudyLayout';
import { ChatMessage } from '../../components/study/ChatMessage';
import { ChatInput } from '../../components/study/ChatInput';
import { PlanBadge } from '../../components/study/PlanBadge';
import { DocumentContextBadge } from '../../components/study/DocumentContextBadge';
import { api } from '../../config/firebase';
import { useStudyStore } from '../../stores/studyStore';
import { ChatMessage as ChatMessageType } from '@voxapilot/shared';

export function StudyChatPage() {
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { currentDocument, setCurrentDocument } = useStudyStore();
  // const {user} = useAuthStore();

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Scroll to bottom when messages change (only if messages exist)
  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Load chat history if chatId exists
  useEffect(() => {
    if (chatId) {
      loadChatHistory();
    }
  }, [chatId]);

  const loadChatHistory = async () => {
    if (!chatId) return;

    try {
      setIsLoadingHistory(true);
      const response = await api.study.getChatHistory(chatId);
      if (response.success && response.data) {
        setMessages(response.data);
      }
    } catch (err: any) {
      console.error('Failed to load chat history:', err);
      setError('Failed to load chat history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleClearDocument = () => {
    setCurrentDocument(null);
  };

  const handleSendMessage = async (message: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.study.sendChatMessage(
        message,
        currentDocument?.id,
        chatId || undefined
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send message');
      }

      if (response.data) {
        // Set chatId if this is a new chat
        const newChatId = (response.data as any).chatId || (response.data as any).aiMessage?.chatId;
        if (!chatId && newChatId) {
          setChatId(newChatId);
        }

        // Add user message and AI response to messages
        const data = response.data as any;
        if (data.userMessage && data.aiMessage) {
          // Backend returns both messages
          setMessages((prev) => [...prev, data.userMessage, data.aiMessage]);
        } else if (data.role === 'assistant') {
          // Backend only returns AI message (legacy format)
          const userMessage: ChatMessageType = {
            id: `temp-${Date.now()}`,
            chatId: data.chatId || chatId || '',
            documentId: currentDocument?.id,
            userId: '', // Will be set by backend
            role: 'user',
            content: message,
            metadata: {
              timestamp: new Date().toISOString(),
            },
          };
          setMessages((prev) => [...prev, userMessage, data]);
        }
      }
    } catch (err: any) {
      console.error('Send message error:', err);
      setError(err.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StudyLayout>
      <div className="flex flex-col h-full bg-background dark:bg-[#1E1E1E]">
        {/* Fixed Header Section */}
        <div className="flex-shrink-0 border-b border-gray-200/60 dark:border-gray-700/60 bg-white/80 dark:bg-[#252526]/80 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-5">
            <div className="flex items-center justify-between">
              {/* Left: Page Title */}
              <div className="flex items-center gap-4">
                <h1 className="text-xl font-semibold text-textPrimary dark:text-[#CCCCCC] tracking-tight">
                  Chat
                </h1>
                {currentDocument && (
                  <DocumentContextBadge document={currentDocument} onClear={handleClearDocument} />
                )}
              </div>

              {/* Right: Plan Badge */}
              <PlanBadge />
            </div>
          </div>
        </div>

        {/* Adaptive Content Area */}
        {messages.length === 0 && !isLoadingHistory ? (
          /* Empty State - Centered Layout */
          <div className="flex-1 flex flex-col items-center justify-center min-h-0">
            <div className="max-w-4xl mx-auto w-full px-4 sm:px-6">
              {/* Error Display */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-red-600 dark:text-red-400 flex-1">{error}</p>
                    <button
                      onClick={() => setError(null)}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors flex-shrink-0"
                      aria-label="Dismiss error"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Empty State Content */}
              <div className="text-center max-w-md mx-auto mb-8 animate-fade-in">
                <div className="mb-4 flex justify-center">
                  <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-orange-600 dark:text-orange-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-base font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                  Start a conversation
                </p>
                <p className="text-sm text-textSecondary dark:text-gray-400 mb-4">
                  Ask questions, get explanations, or generate study materials
                </p>
                {currentDocument && (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/50">
                    <svg
                      className="w-4 h-4 text-orange-600 dark:text-orange-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="text-xs text-orange-700 dark:text-orange-300">
                      Using context from: <span className="font-medium">{currentDocument.title}</span>
                    </span>
                  </div>
                )}
              </div>

              {/* Centered Input */}
              <div className="max-w-4xl mx-auto">
                <ChatInput
                  onSend={handleSendMessage}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>
        ) : (
          /* Messages State - Standard Layout */
          <>
            {/* Scrollable Messages Area */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-6">
                {/* Error Display */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm text-red-600 dark:text-red-400 flex-1">{error}</p>
                      <button
                        onClick={() => setError(null)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors flex-shrink-0"
                        aria-label="Dismiss error"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Loading History State */}
                {isLoadingHistory && messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full min-h-[400px]">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                      <p className="text-sm text-textSecondary dark:text-gray-400">Loading conversation...</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {messages.map((message) => (
                      <ChatMessage key={message.id} message={message} showTimestamp={true} />
                    ))}
                    {isLoading && (
                      <div className="flex justify-start animate-fade-in">
                        <div className="bg-white dark:bg-[#252526] rounded-lg px-4 py-3 border border-gray-200/60 dark:border-gray-700/60 shadow-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-orange-500 dark:bg-orange-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-orange-500 dark:bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-orange-500 dark:bg-orange-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>
            </div>

            {/* Input at Bottom (when messages exist) */}
            <div className="flex-shrink-0 border-t border-gray-200/60 dark:border-gray-700/60 bg-white/80 dark:bg-[#252526]/80 backdrop-blur-sm">
              <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-5">
                <ChatInput
                  onSend={handleSendMessage}
                  disabled={isLoading}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </StudyLayout>
  );
}

